import React, { Suspense, useState, useEffect, useRef } from 'react';
import { Canvas, useLoader } from '@react-three/fiber';
import { OrbitControls, Stage, Environment } from '@react-three/drei';
import Sofa from './Sofa';
import './style.css';

export default function Viewer() {
  const [scale, setScale] = useState(1);

  const canvasRef = useRef();
  const [showOverlay, setShowOverlay] = useState(false);
  const interactionTimeout = useRef(null);

  // Handler to reset interaction timer
  const resetInteractionTimer = () => {
    // Clear existing timer
    if (interactionTimeout.current) {
      clearTimeout(interactionTimeout.current);
    }
    // Hide overlay
    setShowOverlay(false);
    // Set new timer
    interactionTimeout.current = setTimeout(() => {
      setShowOverlay(true);
    }, 2000); // 10 seconds of inactivity
  };

  useEffect(() => {
    const canvasElement = canvasRef.current;
    // Add event listeners for user interaction
    canvasElement.addEventListener('mousemove', resetInteractionTimer);
    canvasElement.addEventListener('touchstart', resetInteractionTimer);
    canvasElement.addEventListener('touchmove', resetInteractionTimer);

    // Initialize interaction timer on mount
    resetInteractionTimer();

    return () => {
      // Clean up event listeners and timer
      canvasElement.removeEventListener('mousemove', resetInteractionTimer);
      canvasElement.removeEventListener('touchstart', resetInteractionTimer);
      canvasElement.removeEventListener('touchmove', resetInteractionTimer);
      clearTimeout(interactionTimeout.current);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setScale(10); // Reduce scale to half on mobile
      } else {
        setScale(20); // Default scale for larger screens
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Call once to set initial state

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="viewer-container" ref={canvasRef}>
      <h1 className="viewer-heading">Sofa Showcase</h1>
      <p className="viewer-subheading">Try products in your place or on your body</p>
      <div className="canvas-container">
      {showOverlay && (
          <img src="Cursor-Gif.gif" alt="Inactivity Overlay" className="inactivity-overlay" />
        )}
        <Suspense>
          <Canvas shadows camera={{ position: [0, 50, 70], fov: 40 }}>
            <Environment files="/christmas_photo_studio_05_2k.hdr" ground={{ height: 35, radius: 100, scale: 200 }} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[10, -20, 5]} intensity={0.7} castShadow />
            <directionalLight position={[0, 5, 5]} intensity={0.7} castShadow />
            <Stage environment="studio" intensity={0.5} adjustCamera={false}>
              <Sofa scale={[scale, scale, scale]} />
            </Stage>
            <OrbitControls
              autoRotate
              enablePan={false}
              enableZoom={false}
              autoRotateSpeed={4}
              makeDefault
            />
          </Canvas>
        </Suspense>
      </div>
      <a href="https://calendly.com/vincent-119" target="_blank" rel="noopener noreferrer" className="viewer-demo-button">
        Book a Demo
      </a>
    </div>
  );
}
